import * as THREE from 'three';
import { mageEffects, spellEffects } from '../../../content/spells/effects.js';
import type { CantripAction } from '../entities/character-animator.js';
import type { UnitAnimator } from '../entities/unit-animator.js';
import { isShadowCantrip, ShadowCharge } from './shadow-charges.js';

/** What the scene needs of a cantrip in progress (`CharacterAnimator.cantripCast`): the clip's progress, where it gathers (the casting palm, Mockery's mouth), the way the figure faces and any spot it is aimed at. */
export interface CantripCast { kind: CantripAction; progress: number; hand: THREE.Vector3; facing: THREE.Vector3; /** Where on the ground it is aimed (a summoned Shadow's spot). */ at?: { x: number; z: number } }

/** Where the clip lets go (`CANTRIP_RELEASE`) and how long the burst after it lasts, in clip progress (the clip is 2.5 s). */
const RELEASE = .8, BURST = .14;
const MOTES = 22, STREAKS = 3, STREAK_POINTS = 12;
const additive = { transparent: true, depthWrite: false, blending: THREE.AdditiveBlending } as const;
const UP = new THREE.Vector3(0, 1, 0);
const smooth = (x: number, a: number, b: number) => THREE.MathUtils.smoothstep(x, a, b);
/** The Mage's cantrips gather their school's energy in the palm: embers, frost or sparks. */
const MAGE_SCHOOL: Readonly<Record<string, 'fire' | 'ice' | 'lightning'>> = { 'flame-dart': 'fire', 'hearts-fire': 'fire', 'ice-lance': 'ice', snowblind: 'ice', zap: 'lightning', overload: 'lightning' };
const MAGE_COLOR = { fire: mageEffects.fire.glow, ice: mageEffects.ice.frost, lightning: mageEffects.storm.spark } as const;
/** A stable number from 0 to 1 for a mote and a caster. */
const hash = (i: number, seed: number) => { const x = Math.sin(i * 127.1 + seed * 311.7) * 43758.5453; return x - Math.floor(x); };

function points(count: number, color: number) {
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(new Float32Array(count * 3), 3));
  geometry.setAttribute('aLife', new THREE.BufferAttribute(new Float32Array(count), 1));
  const material = new THREE.ShaderMaterial({ ...additive,
    vertexShader: 'attribute float aLife; uniform float uSize; varying float vLife; void main(){vLife=aLife;gl_PointSize=uSize*(.55+.45*aLife);gl_Position=projectionMatrix*modelViewMatrix*vec4(position,1.0);}',
    fragmentShader: 'uniform vec3 uColor; uniform float uSoft; varying float vLife; void main(){float d=length(gl_PointCoord-.5)*2.0;gl_FragColor=vec4(uColor,pow(max(0.0,1.0-d),uSoft)*vLife);}',
    uniforms: { uSize: { value: 3 }, uSoft: { value: 2 }, uColor: { value: new THREE.Color(color) } } });
  const object = new THREE.Points(geometry, material); object.frustumCulled = false;
  return object;
}

/**
 * The small charge a cantrip gathers at the casting hand and the burst it lets go with: wind drawn in around the hand for Razor Wind, a few
 * motes of breath rising from the palm for Breath of Life, a violet haze drawn in to the lips and spat out in a narrow jet for Vicious Mockery,
 * and a faint glow that grows with the charge and flashes as the cantrip leaves. It is
 * hand-sized and quiet on purpose (cantrips are cast all the time); the flight, the wake and the impact are `SpellEffects`'.
 * Everything is a function of the clip's progress and the game clock, so a paused game freezes it.
 */
class CantripCharge {
  readonly group = new THREE.Group();
  private motes: THREE.Points;
  private glow: THREE.Points;
  private streaks: THREE.Line[] = [];
  constructor(readonly kind: Exclude<CantripAction, ShadowCharge['kind']>, private seed: number) {
    const school = MAGE_SCHOOL[kind], palette = school ? { color: MAGE_COLOR[school] } : spellEffects[kind as keyof typeof spellEffects];
    this.group.name = `${kind}-charge`;
    // Breath of Life's motes are a little warmer than the cutting wind: breath and leaf-light rather than a blade.
    this.motes = points(MOTES, kind === 'breath-of-life' ? 0xdfeec0 : palette.color);
    this.glow = points(1, palette.color); (this.glow.material as THREE.ShaderMaterial).uniforms.uSoft.value = 2.6;
    this.group.add(this.glow, this.motes);
    if (kind === 'razor-wind') for (let i = 0; i < STREAKS; i++) {
      const geometry = new THREE.BufferGeometry(); geometry.setAttribute('position', new THREE.BufferAttribute(new Float32Array(STREAK_POINTS * 3), 3));
      const line = new THREE.Line(geometry, new THREE.LineBasicMaterial({ ...additive, color: palette.color, opacity: 0 })); line.frustumCulled = false;
      this.streaks.push(line); this.group.add(line);
    }
  }
  /** Poses the charge for clip progress `p` at game time `time`; returns false once it has nothing left to show. */
  update(cast: CantripCast, time: number, ppm: number) {
    const p = cast.progress, hand = cast.hand, forward = cast.facing, side = new THREE.Vector3().crossVectors(UP, forward).normalize();
    const charge = smooth(p, .1, .74), q = THREE.MathUtils.clamp((p - RELEASE) / BURST, 0, 1), released = p >= RELEASE;
    const flash = released ? Math.pow(1 - q, 2) : 0, held = released ? 0 : charge;
    const at = this.motes.geometry.getAttribute('position') as THREE.BufferAttribute, life = this.motes.geometry.getAttribute('aLife') as THREE.BufferAttribute;
    const point = new THREE.Vector3();
    for (let i = 0; i < MOTES; i++) {
      // Fixed per mote and caster, so every frame (and every screen) scatters them the same way.
      const a = hash(i, this.seed), b = hash(i + 31, this.seed), c = hash(i + 67, this.seed);
      if (this.kind === 'razor-wind') {
        if (!released) {
          // Air drawn in round the hand in a flattening spiral that tightens and quickens as the charge builds.
          const age = (time * 1.6 + i * .618) % 1, radius = (.05 + .3 * (1 - age)) * (1 - .45 * charge), angle = i * 2.399 + time * (5 + 9 * charge) + age * 3;
          point.copy(hand).addScaledVector(side, Math.cos(angle) * radius).addScaledVector(forward, Math.sin(angle) * radius).addScaledVector(UP, Math.sin(i * 1.7 + time * 3) * .04 * (1 - age));
          life.setX(i, Math.sin(Math.PI * age) * held * .9);
        } else {
          // Let go in a flat fan along the cut, fastest at the middle of it.
          const reach = Math.pow(q, .7) * (.9 + a * .8);
          point.copy(hand).addScaledVector(forward, reach).addScaledVector(side, (b - .5) * reach * .9).addScaledVector(UP, (c - .5) * .08 * reach);
          life.setX(i, (1 - q) * .95);
        }
      } else if (this.kind === 'vicious-mockery') {
        if (!released) {
          // A haze drawn in to the lips from the air in front of the face, slow at first, quicker and closer as the jeer builds.
          const age = (time * (.8 + 1.2 * charge) + i * .618) % 1, radius = (.04 + .2 * (1 - age)) * (1 - .4 * charge), angle = i * 2.399 + time * 1.5;
          point.copy(hand).addScaledVector(forward, .05 + .18 * (1 - age) * (.4 + .6 * a)).addScaledVector(side, Math.cos(angle) * radius).addScaledVector(UP, Math.sin(angle) * radius * .7);
          life.setX(i, Math.sin(Math.PI * age) * held * .75);
        } else {
          // Then spat out at the foe in a narrow, wavering jet.
          const reach = Math.pow(q, .65) * (.7 + a * .9), wobble = Math.sin(reach * 9 + i) * .05 * reach;
          point.copy(hand).addScaledVector(forward, reach).addScaledVector(side, (b - .5) * reach * .3 + wobble).addScaledVector(UP, (c - .5) * reach * .2);
          life.setX(i, (1 - q) * .9);
        }
      } else if (MAGE_SCHOOL[this.kind]) {
        const school = MAGE_SCHOOL[this.kind];
        if (!released) {
          // Embers lift off the palm, frost is drawn in round it and settles, sparks jump about it.
          const age = (time * (school === 'lightning' ? 2.4 : 1) + i * .618) % 1, jump = Math.floor(time * 18 + i);
          if (school === 'fire') {
            // Flame licking up off the palm: each tongue rises quickly, sways on the draught and narrows to a point, a few embers breaking free above.
            const lick = (time * (2.2 + a * 1.4) + i * .618) % 1, ember = i % 5 === 0, sway = Math.sin(time * 11 + i * 3.1) * .03 * lick + Math.sin(time * 4.3 + i) * .015;
            const spread = (ember ? .02 : .045) * (1 - lick * .8), angle = i * 2.399 + time * 3;
            point.copy(hand).addScaledVector(UP, lick * (ember ? .32 : .17) + .015).addScaledVector(side, Math.cos(angle) * spread + sway).addScaledVector(forward, Math.sin(angle) * spread + sway * .5);
          }
          else if (school === 'ice') { const radius = (.04 + .2 * (1 - age)) * (1 - .4 * charge), angle = i * 2.399 + time * 1.2; point.copy(hand).addScaledVector(side, Math.cos(angle) * radius).addScaledVector(forward, Math.sin(angle) * radius).addScaledVector(UP, -age * .05); }
          else point.copy(hand).addScaledVector(side, (hash(jump, i) - .5) * .18).addScaledVector(forward, (hash(jump + 7, i) - .5) * .18).addScaledVector(UP, (hash(jump + 13, i) - .5) * .14);
          const lick = (time * (2.2 + a * 1.4) + i * .618) % 1, flicker = .6 + .4 * Math.sin(time * 23 + i * 7.1);
          life.setX(i, school === 'fire' ? Math.pow(Math.sin(Math.PI * lick), .6) * flicker * held : Math.sin(Math.PI * age) * held * (school === 'lightning' ? (hash(jump, i + 3) > .4 ? 1 : 0) : .85));
        } else {
          // Let go: a narrow jet at the foe, a lob up to the ally (Heart's Fire), or flung out over the ground all round (Overload).
          const reach = Math.pow(q, .65) * (.6 + a * .8);
          if (this.kind === 'overload') { const angle = a * Math.PI * 2; point.copy(hand).addScaledVector(side, Math.cos(angle) * reach * 1.4).addScaledVector(forward, Math.sin(angle) * reach * 1.4).addScaledVector(UP, -(c * .5) * reach); }
          else if (this.kind === 'hearts-fire') point.copy(hand).addScaledVector(forward, reach * .8).addScaledVector(side, (b - .5) * reach * .4).addScaledVector(UP, reach * (.4 + c * .3));
          else point.copy(hand).addScaledVector(forward, reach).addScaledVector(side, (b - .5) * reach * .25).addScaledVector(UP, (c - .5) * reach * .18);
          life.setX(i, (1 - q) * .9);
        }
      } else if (!released) {
        // Breath gathers as a few motes rising from the palm and turning over it.
        const age = (time * .7 + i * .618) % 1, radius = .04 + .035 * Math.sin(i * 2.1 + time * 2);
        point.copy(hand).addScaledVector(UP, age * .16).addScaledVector(side, Math.cos(i * 2.399 + time) * radius).addScaledVector(forward, Math.sin(i * 2.399 + time) * radius);
        life.setX(i, Math.sin(Math.PI * age) * held * .8);
      } else {
        // Then breathed out toward the ally in a soft cone that rises a little as it goes.
        const reach = Math.pow(q, .6) * (.4 + a * .5);
        point.copy(hand).addScaledVector(forward, reach).addScaledVector(side, (b - .5) * reach * .6).addScaledVector(UP, (c - .3) * reach * .4);
        life.setX(i, (1 - q) * .85);
      }
      at.setXYZ(i, point.x, point.y, point.z);
    }
    at.needsUpdate = true; life.needsUpdate = true;
    const moteSize = this.kind === 'razor-wind' ? .045 : this.kind === 'vicious-mockery' ? .04 : .05;
    (this.motes.material as THREE.ShaderMaterial).uniforms.uSize.value = Math.max(1.5, moteSize * ppm);
    // The glow grows with the charge, throbs faintly while it is held and flashes once as the cantrip leaves.
    const glowAt = this.glow.geometry.getAttribute('position') as THREE.BufferAttribute, glowLife = this.glow.geometry.getAttribute('aLife') as THREE.BufferAttribute;
    glowAt.setXYZ(0, hand.x, hand.y, hand.z); glowAt.needsUpdate = true;
    const throb = MAGE_SCHOOL[this.kind] === 'fire' ? .2 + .07 * Math.sin(time * 17 + this.seed) + .05 * Math.sin(time * 29) : .16 + .04 * Math.sin(time * 9 + this.seed);
    glowLife.setX(0, held * throb + flash * .55); glowLife.needsUpdate = true;
    (this.glow.material as THREE.ShaderMaterial).uniforms.uSize.value = Math.max(2, (.1 + .1 * held + .22 * flash) * ppm);
    // Razor Wind: three thin streaks of wind circle the hand, closing in; on the release they are flung along the cut and thin out.
    this.streaks.forEach((line, s) => {
      const positions = line.geometry.getAttribute('position') as THREE.BufferAttribute;
      const radius = .07 + .16 * (1 - .45 * charge), start = s * 2.1 + time * (4 + 8 * charge), tilt = (s - 1) * .25;
      for (let j = 0; j < STREAK_POINTS; j++) {
        const t = j / (STREAK_POINTS - 1), angle = start + t * 1.6;
        if (!released) point.copy(hand).addScaledVector(side, Math.cos(angle) * radius).addScaledVector(forward, Math.sin(angle) * radius).addScaledVector(UP, Math.sin(angle) * tilt * radius);
        else {
          // Flung out as thin crescents bowed along the cut, widening as they go.
          const arc = (t - .5) * 1.8, bow = .2 + .12 * q;
          point.copy(hand).addScaledVector(forward, q * (1.1 + s * .22) + (Math.cos(arc) - 1) * bow * .7).addScaledVector(side, Math.sin(arc) * bow).addScaledVector(UP, (s - 1) * .06 + Math.sin(arc) * (s - 1) * .04);
        }
        positions.setXYZ(j, point.x, point.y, point.z);
      }
      positions.needsUpdate = true;
      (line.material as THREE.LineBasicMaterial).opacity = released ? (1 - q) * .7 : held * .45;
    });
    return p < RELEASE + BURST;
  }
  /** Dims everything the charge shows by `amount` (0 to 1), after `update`: a cast cut short goes out where it was rather than gathering on in the air. */
  dim(amount: number) {
    for (const points of [this.motes, this.glow]) {
      const life = points.geometry.getAttribute('aLife') as THREE.BufferAttribute;
      for (let i = 0; i < life.count; i++) life.setX(i, life.getX(i) * amount);
      life.needsUpdate = true;
    }
    for (const line of this.streaks) (line.material as THREE.LineBasicMaterial).opacity *= amount;
  }
  dispose() {
    this.group.removeFromParent();
    this.group.traverse(node => { if (node instanceof THREE.Points || node instanceof THREE.Line) { node.geometry.dispose(); (node.material as THREE.Material).dispose(); } });
  }
}

/** The cantrip clip's length in game seconds (`CHARACTER_ACTION_SECONDS`), to carry a charge on after its clip is over. */
const CLIP_SECONDS = 2.5;
/** Seconds a charge whose cast was cut short before it let go takes to go out, held where it was. */
const FIZZLE = .3;
/**
 * Plays on a charge whose cast is over or was taken over by another. One that had let go carries on through its burst; one cut short before
 * it let go goes out where it was over `FIZZLE`, so a string of recast or interrupted cantrips never leaves charges hanging in the air.
 */
function playOn(charge: CantripCharge | ShadowCharge, cast: CantripCast, since: number, time: number, ppm: number) {
  if (cast.progress >= RELEASE || !(charge instanceof CantripCharge)) return charge.update({ ...cast, progress: cast.progress + since / CLIP_SECONDS }, time, ppm);
  const left = 1 - since / FIZZLE;
  if (left <= 0) return false;
  charge.update(cast, time, ppm); charge.dim(left);
  return true;
}

/**
 * One charge per caster, drawn from each animator's `cantripCast()`; the Shadowmancer's are dark smoke (`ShadowCharge`). A charge whose clip
 * ends (or is cut short, or another cantrip starts over it) before it has faded plays on from where it was, on its own clock, until it is gone:
 * nothing vanishes at once.
 */
export class CantripCharges {
  private charges = new Map<string, CantripCharge | ShadowCharge>();
  private last = new Map<string, { cast: CantripCast; at: number }>();
  /** Charges another cast took over from, fading on their own. */
  private fading: { charge: CantripCharge | ShadowCharge; cast: CantripCast; at: number }[] = [];
  constructor(private readonly scene: THREE.Scene, private readonly groundHeight?: (x: number, z: number) => number) {}
  get visualObjects(): THREE.Object3D[] { return [...this.charges.values(), ...this.fading.map(item => item.charge)].map(charge => charge.group); }
  /** `time` is the game clock in seconds; `pixelsPerMeter` sizes the motes (the camera is orthographic). */
  update(animators: ReadonlyMap<string, UnitAnimator>, visible: (id: string) => boolean, time: number, pixelsPerMeter: number) {
    for (const [id, animator] of animators) {
      const cast = visible(id) ? animator.cantripCast?.() : undefined;
      let charge = this.charges.get(id);
      if (!cast) {
        const last = this.last.get(id);
        if (charge && last && visible(id)) charge.group.visible = playOn(charge, last.cast, Math.max(0, time - last.at), time, pixelsPerMeter);
        if (charge && (!last || !visible(id) || !charge.group.visible)) this.remove(id);
        continue;
      }
      const last = this.last.get(id);
      this.last.set(id, { cast, at: time });
      if (charge && last && (charge.kind !== cast.kind || cast.progress < last.cast.progress - .05)) {
        this.fading.push({ charge, cast: last.cast, at: last.at }); this.charges.delete(id); charge = undefined;
      }
      if (!charge) {
        charge = isShadowCantrip(cast.kind) ? new ShadowCharge(cast.kind, id.length, this.groundHeight) : new CantripCharge(cast.kind, id.length); this.scene.add(charge.group); this.charges.set(id, charge);
      }
      charge.group.visible = charge.update(cast, time, pixelsPerMeter);
    }
    for (const id of this.charges.keys()) if (!animators.has(id)) this.remove(id);
    this.fading = this.fading.filter(item => {
      item.charge.group.visible = playOn(item.charge, item.cast, Math.max(0, time - item.at), time, pixelsPerMeter);
      if (!item.charge.group.visible) item.charge.dispose();
      return item.charge.group.visible;
    });
  }
  remove(id: string) { this.charges.get(id)?.dispose(); this.charges.delete(id); this.last.delete(id); }
  /** A charge to draw while the scene prepares, so the first cantrip of a fight links no shader (its motes, glow and streaks); dispose it afterwards. */
  static warmup() {
    const charge = new CantripCharge('razor-wind', 0), shadow = new ShadowCharge('shadow-blast', 0), group = new THREE.Group(); group.add(charge.group, shadow.group);
    return { group, dispose: () => { charge.dispose(); shadow.dispose(); } };
  }
  clear() { for (const charge of [...this.charges.values(), ...this.fading.map(item => item.charge)]) charge.dispose(); this.charges.clear(); this.last.clear(); this.fading = []; }
  dispose() { this.clear(); }
}
